<template>
  <div class="details-component" v-if="isLandlord()">
    <div class="details-component__header">
      <h1 v-if="isNew">{{ $t('pricingrules.new_rule') }}</h1>
      <h1 v-else>{{ $t('pricingrules.edit_rule') }}</h1>
    </div>
    <div class="details-component__content content pricingrulesform">
      <form class="form" @submit.prevent>
        <table class="details-table tenant no-col-padding edit-table fixed-table label-col-wide-260">
          <!-- Parking rule -->
          <tr v-if="isParkingRule" class="details-table__row" :class="{'details-table__row_error' : errors.has('parkingTimeRange')}">
            <td class="details-table__col details-table__col_label">
              <label for="parkingTimeRange" class="form__label">{{ $t('conferencerules.parking_time_span') }}</label>
            </td>
            <td class="details-table__col auto-height">
              <el-date-picker
                id="parkingTimeRange"
                name="parkingTimeRange"
                v-model="ruleTimeRange"
                v-bind:format="timeDisplayFormat"
                type="datetimerange"
                v-validate="'required'"
                v-bind:value-format="timeDisplayFormat">
              </el-date-picker>
              <tooltip v-show="errors.has('parkingTimeRange')" class="form__validation">{{ $t('conferencerules.parking_period_required') }}</tooltip>
            </td>
          </tr>

          <!-- Registration rule -->
          <tr v-if="isRegistrationRule" class="details-table__row">
            <td class="details-table__col details-table__col_label">
              <label for="registerStartTime" class="form__label">{{ $t('conferencerules.registration_time_start') }}</label>
            </td>
            <td class="details-table__col auto-height">
              <el-date-picker
                id="registerStartTime"
                name="registerStartTime"
                v-model="ruleTimeStart"
                v-bind:format="timeDisplayFormat"
                type="datetime"
                :picker-options="startTimeOptions"
                :clearable="true"
                v-bind:value-format="timeDisplayFormat">
              </el-date-picker>
            </td>
          </tr>
          <br/>
          <tr v-if="isRegistrationRule" class="details-table__row" :class="{'details-table__row_error' : errors.has('registerEndTime')}">
            <td class="details-table__col details-table__col_label">
              <label for="registerEndTime" class="form__label">{{ $t('conferencerules.registration_time_end') }}</label>
            </td>
            <td class="details-table__col">
              <el-date-picker
                id="registerEndTime"
                name="registerEndTime"
                v-model="ruleTimeEnd"
                v-bind:format="timeDisplayFormat"
                type="datetime"
                :picker-options="endTimeOptions"
                v-validate="'required'"
                v-bind:value-format="timeDisplayFormat">
              </el-date-picker>
              <tooltip v-show="errors.has('registerEndTime')" class="form__validation">{{ $t('conferencerules.register_end_time_required') }}</tooltip>
            </td>
          </tr>
        </table>

        <div class="form__buttons buttonset">
          <router-link :to="{ name: 'product', params: { id: $route.params.id }}" class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <button type="submit" class="buttonset__button btn btn--main" v-on:click.prevent.stop="onSubmit()">
          {{ $t('common.save') }}</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import moment from 'moment';
  import Tooltip from 'common/Tooltip';

  export default {
    components: {
      Tooltip,
    },
    computed: {
      isNew() {
        return !this.$route.params.ruleId;
      },
      ...mapGetters({
        conferencerule: 'conferencerules/conferencerule/conferencerule',
        user: 'user/user/user',
      }),
      isParkingRule() {
        return this.$route.params.ruleType ? (this.$route.params.ruleType === 'parking') : (this.form.type === 'parking');
      },
      isRegistrationRule() {
        return this.$route.params.ruleType ? (this.$route.params.ruleType === 'registration') : (this.form.type === 'registration');
      },
    },
    created() {
      this.productId = this.$route.params.id;
      if (!this.isLandlord()) {
        this.$router.push({ name: 'product', params: { id: this.productId } });
      }
      if (!this.isNew) {
        this.fetchConferenceRule();
      }
    },
    methods: {
      fetchConferenceRule() {
        this.$store.dispatch('conferencerules/conferencerule/fetch', { productId: this.productId, ruleId: this.$route.params.ruleId, user: this.user }).then(() => {
          this.form = this.conferencerule;
          if (this.conferencerule.type === 'registration') {
            this.ruleTimeStart = this.getTimeFormatForEdit(this.form.startTime);
            this.ruleTimeEnd = this.getTimeFormatForEdit(this.form.endTime);
          } else {
            this.ruleTimeRange = [this.getTimeFormatForEdit(this.form.startTime),
              this.getTimeFormatForEdit(this.form.endTime)];
          }
        });
      },
      onSubmit() {
        this.$validator.validateAll()
          .then((success) => {
            if (!success) {
              return;
            }

            if (this.isNew) {
              if (this.isParkingRule) {
                this.form.type = 'parking';
              } else {
                this.form.type = 'registration';
              }
            }

            if (this.isParkingRule) {
              this.form.startTime = this.getStartTimeFormatForSave(this.ruleTimeRange[0]);
              this.form.endTime = this.getEndTimeFormatForSave(this.ruleTimeRange[1]);
            } else {
              this.form.startTime = this.getStartTimeFormatForSave(this.ruleTimeStart);
              this.form.endTime = this.getEndTimeFormatForSave(this.ruleTimeEnd);
            }

            this.form.productId = this.productId;
            this.$store.dispatch('conferencerules/conferencerule/save', { user: this.user, rule: this.form }).then(() => {
              this.form = {};
              this.$router.push({ name: 'product', params: { id: this.productId } });
            });
          })
          .catch(() => {});
      },
      getStartTimeFormatForSave(date) {
        if (date && date !== '') {
          return moment(date, 'DD.MM.YYYY HH:mm').format('YYYY-MM-DD HH:mm:ss');
        }
        return null;
      },
      getEndTimeFormatForSave(date) {
        if (date && date !== '') {
          return moment(date, 'DD.MM.YYYY HH:mm').seconds(59).format('YYYY-MM-DD HH:mm:ss');
        }
        return null;
      },
      getTimeFormatForEdit(date) {
        if (date && date !== '') {
          return moment(date, 'YYYY-MM-DD HH:mm:ss').format('DD.MM.YYYY HH:mm');
        }
        return null;
      },
    },
    data() {
      return {
        form: {
          startTime: null,
          endTime: null,
          type: null,
        },
        productId: null,
        ruleTimeRange: [],
        ruleTimeStart: null,
        ruleTimeEnd: null,
        timeDisplayFormat: 'dd.MM.yyyy HH:mm',
        startTimeOptions: {
          disabledDate: (date) => {
            const isInPast = moment(date).isBefore(moment(), 'day');
            if (this.form.endTime) {
              return moment(this.form.endTime).isBefore(moment(date), 'day') || isInPast;
            }
            return isInPast;
          },
          firstDayOfWeek: 1,
        },
        endTimeOptions: {
          disabledDate: (date) => {
            const isInPast = moment(date).isBefore(moment(), 'day');
            if (this.form.startTime) {
              return moment(this.form.startTime).isAfter(moment(date), 'day') || isInPast;
            }
            return isInPast;
          },
          firstDayOfWeek: 1,
        },
      };
    },
  };
</script>



// WEBPACK FOOTER //
// src/components/conferencerules/AddConferenceRuleView.vue